import type { GetAppConfigBody } from "@/api/dto/users"
import { isI18nLanguage, supportedI18nLanguages, type I18nLanguage } from "@/store/settings-state"
import { isElectronApp } from "@/utils/electron"

export const ENGLISH_ONLY_APP_CONFIG: GetAppConfigBody = {
	localization: {
		electron: { enabledLocales: ["en"] },
		web: { enabledLocales: ["en"] },
	},
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value)

const hasEnabledLocales = (value: unknown): value is { enabledLocales: string[] } =>
	isRecord(value) &&
	Array.isArray(value.enabledLocales) &&
	value.enabledLocales.every((locale) => typeof locale === "string")

/** Validate the untrusted App Config response before it controls application behavior. */
export const parseAppConfig = (value: unknown): GetAppConfigBody => {
	if (!isRecord(value) || !isRecord(value.localization)) {
		throw new Error("App Config is missing localization settings.")
	}

	const { electron, web } = value.localization
	if (!hasEnabledLocales(electron) || !hasEnabledLocales(web)) {
		throw new Error("App Config localization settings are invalid.")
	}

	return {
		localization: {
			electron: { enabledLocales: [...electron.enabledLocales] },
			web: { enabledLocales: [...web.enabledLocales] },
		},
	}
}

/**
 * Return only locales this web build understands. English is always available as
 * the embedded, safe fallback even if it is accidentally omitted by App Config.
 */
export const getEnabledLocales = (
	config: GetAppConfigBody | undefined,
	electronApp = isElectronApp,
): I18nLanguage[] => {
	const configuredLocales = config
		? electronApp
			? config.localization.electron.enabledLocales
			: config.localization.web.enabledLocales
		: []
	const enabledSet = new Set(configuredLocales.filter(isI18nLanguage))
	enabledSet.add("en")

	return supportedI18nLanguages.filter((locale) => enabledSet.has(locale))
}

export const isLocalizationEnabled = (
	config: GetAppConfigBody | undefined,
	electronApp = isElectronApp,
): boolean => getEnabledLocales(config, electronApp).some((locale) => locale !== "en")
